/**
 * NotificationBell — real-time bell in the top nav.
 *
 * Subscribes to the SSE stream at /api/notifications/stream which pushes a
 * fresh snapshot every ~20s (or on count changes). Falls back to a one-shot
 * /summary call if SSE fails. Clicking the bell opens a dropdown; clicking
 * an item deep-links to the relevant page; "Mark all read" flips the badge.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, UserPlus, CalendarHeart, Briefcase, TrendingUp, Megaphone, Clock, CheckCircle, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  connection_requests: { label: 'Connection Requests', icon: UserPlus, link: '/dashboard/connections', color: 'text-cyan-400' },
  event_invites:       { label: 'Event Invites',       icon: CalendarHeart, link: '/dashboard/event-invites', color: 'text-pink-400' },
  business_intros:     { label: 'Business Intros',     icon: Briefcase, link: '/dashboard/my-businesses', color: 'text-emerald-400' },
  offer_intros:        { label: 'Offer Intros',        icon: Briefcase, link: '/dashboard/investor-offers', color: 'text-amber-400' },
  match_alerts:        { label: 'Cross-Match Alerts',  icon: TrendingUp, link: '/dashboard', color: 'text-purple-400' },
  renewals:            { label: 'Membership Renewal',  icon: Clock, link: '/dashboard/profile', color: 'text-orange-400' },
  announcements:       { label: 'Announcement',        icon: Megaphone, link: '/', color: 'text-slate-300' },
  system:              { label: 'Activity',            icon: CheckCircle, link: '/dashboard', color: 'text-emerald-300' },
};

const ORDER = [
  'event_invites', 'connection_requests', 'business_intros',
  'offer_intros', 'match_alerts', 'renewals', 'announcements', 'system',
];

// Mapping for the `system` bucket items — each one has a `type` field with
// natural names like 'attendance_marked' / 'dm_auto_intro'.
// Choose icon + label dynamically.
const SYSTEM_TYPE_META = {
  attendance_marked:   { icon: CheckCircle, color: 'text-emerald-400' },
  dm_auto_intro:       { icon: MessageSquare, color: 'text-cyan-400' },
  dm:                  { icon: MessageSquare, color: 'text-cyan-400' },
};

const NotificationBell = ({ user }) => {
  const [snap, setSnap] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const esRef = useRef(null);

  // Subscribe to SSE on mount / user change
  useEffect(() => {
    if (!user) {
      setSnap(null);
      return undefined;
    }

    let cancelled = false;
    const fetchSnap = async () => {
      try {
        const r = await axios.get(`${API}/notifications/summary`, { withCredentials: true });
        if (!cancelled) setSnap(r.data);
      } catch { /* ignore */ }
    };
    fetchSnap();

    let es;
    try {
      es = new EventSource(`${API}/notifications/stream`, { withCredentials: true });
      esRef.current = es;
      es.addEventListener('snapshot', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (!cancelled) setSnap(data);
        } catch { /* invalid frame */ }
      });
      es.onerror = () => {
        // EventSource auto-reconnects with backoff; no-op
      };
    } catch {
      // EventSource not supported — poll every 30s
      const id = setInterval(fetchSnap, 30_000);
      return () => { cancelled = true; clearInterval(id); };
    }

    return () => {
      cancelled = true;
      try { es && es.close(); } catch { /* noop */ }
    };
  }, [user?.user_id]);

  const markAllSeen = async (e) => {
    e?.stopPropagation?.();
    try {
      const r = await axios.post(`${API}/notifications/mark-all-seen`, {}, { withCredentials: true });
      setSnap((s) => s ? {
        ...s,
        last_seen_at: r.data.last_seen_at,
        // zero out categories that are read-by-marker (not status-driven)
        business_intros: { ...s.business_intros, count: 0 },
        offer_intros:    { ...s.offer_intros,    count: 0 },
        match_alerts:    { ...s.match_alerts,    count: 0 },
        announcements:   { ...s.announcements,   count: 0 },
        total_unread:    (s.event_invites?.count || 0) + (s.connection_requests?.count || 0) + (s.renewals?.count || 0),
      } : s);
    } catch { /* ignore */ }
  };

  const go = (link) => {
    setOpen(false);
    navigate(link);
  };

  if (!user) return null;

  const total = snap?.total_unread || 0;
  const hasItems = total > 0 || ORDER.some((k) => snap?.[k]?.items?.length > 0);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        aria-label={`Notifications${total ? ` (${total} unread)` : ''}`}
        className="relative text-slate-300 hover:text-[#d4af37] transition-colors p-1"
      >
        <Bell size={20} />
        {total > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-[#d4af37] text-slate-950 text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none"
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop for click-outside */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Dropdown — width responsive to viewport so it never overflows the
              left edge on narrow phones. Desktop keeps the 360px panel right-
              anchored under the bell; mobile pins to viewport edges with safe
              gutters and scrolls vertically. */}
          <div
            data-testid="notification-dropdown"
            className="
              fixed left-3 right-3 top-16 max-h-[75vh] overflow-y-auto rounded-xl
              bg-slate-900 border border-[#d4af37]/30 shadow-2xl z-50
              sm:absolute sm:left-auto sm:right-0 sm:top-10 sm:w-[360px] sm:max-w-[calc(100vw-1rem)] sm:rounded-none
            "
          >
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="text-white text-sm font-semibold">Notifications</h3>
                <p className="text-xs text-slate-500">{total > 0 ? `${total} unread` : 'All caught up'}</p>
              </div>
              {total > 0 && (
                <button
                  onClick={markAllSeen}
                  data-testid="notification-mark-all-read"
                  className="text-xs text-[#d4af37] hover:underline inline-flex items-center gap-1"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
            </div>

            {/* Body */}
            {!hasItems ? (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto text-slate-700 mb-2" size={28} />
                <p className="text-sm text-slate-400">You're all caught up.</p>
                <p className="text-xs text-slate-500 mt-1">New invites, requests and matches will appear here.</p>
              </div>
            ) : (
              ORDER.map((kind) => {
                const block = snap?.[kind];
                if (!block || (!block.count && !block.items?.length)) return null;
                const meta = CATEGORY_META[kind];
                const Icon = meta.icon;
                // For the `system` bucket, deep-link to the first item's
                // own `link` if present (e.g. /events/abc), otherwise
                // fall back to the dashboard.
                const targetLink = (kind === 'system' && block.items?.[0]?.link)
                  ? block.items[0].link
                  : meta.link;
                return (
                  <div key={kind} className="border-b border-white/5 last:border-b-0">
                    <button
                      onClick={() => go(targetLink)}
                      data-testid={`notification-category-${kind}`}
                      className="w-full text-left px-4 py-3 hover:bg-slate-800/60 transition-colors flex items-start gap-3"
                    >
                      <Icon className={`${meta.color} flex-shrink-0 mt-0.5`} size={16} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-white text-sm font-semibold truncate">{meta.label}</p>
                          {block.count > 0 && (
                            <span className="text-[10px] bg-[#d4af37]/15 text-[#d4af37] px-1.5 py-0.5 rounded font-bold">
                              {block.count}
                            </span>
                          )}
                        </div>
                        <PreviewLines kind={kind} items={block.items} />
                      </div>
                    </button>
                  </div>
                );
              })
            )}

            {/* Footer */}
            <div className="sticky bottom-0 bg-slate-900 border-t border-white/10 px-4 py-2 text-center">
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="text-xs text-slate-400 hover:text-[#d4af37]"
              >
                Go to Dashboard →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Compact preview lines per category — 1-2 line snippets of the top 2 items. */
const PreviewLines = ({ kind, items }) => {
  if (!items?.length) return <p className="text-xs text-slate-500 mt-0.5">No new items</p>;
  const top = items.slice(0, 2);
  return (
    <div className="mt-1 space-y-0.5">
      {top.map((it, i) => (
        <p key={i} className="text-xs text-slate-400 truncate">
          {renderLine(kind, it)}
        </p>
      ))}
    </div>
  );
};

const renderLine = (kind, it) => {
  switch (kind) {
    case 'event_invites':
      return `${it.from_user_name || 'Someone'} → ${it.event_title || 'event'}`;
    case 'connection_requests':
      return `${it.from_user_name || 'Someone'} wants to connect`;
    case 'business_intros':
      return `${it.from_user_name || 'Someone'} → ${it.business_company_name || 'your business'}`;
    case 'offer_intros':
      return `${it.from_user_name || 'Someone'} → your investor offer`;
    case 'match_alerts':
      return `Cross-match: ${it.direction || ''}`.trim();
    case 'renewals':
      return it.days_left < 0
        ? `Membership expired ${Math.abs(it.days_left)} days ago`
        : `Membership expires in ${it.days_left} day${it.days_left === 1 ? '' : 's'}`;
    case 'announcements':
      return it.message ? (it.message.length > 80 ? it.message.slice(0, 80) + '…' : it.message) : 'New announcement';
    case 'system':
      return it.title || it.body || 'New activity';
    default:
      return '';
  }
};

export default NotificationBell;
